/**
 * React Query hooks for Position operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import positionService from '../services/position.service';

interface UsePositionListQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const usePositionListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 10,
  sortBy = 'created_at',
  order = 'desc',
  search,
  status,
  enabled = true,
  refetchInterval = 5000,
}: UsePositionListQueryParams) => {
  return useQuery({
    queryKey: ['positions', workspaceId, page, size, sortBy, order, search, status],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await positionService.listPositions(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
        status,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UsePositionDetailQueryParams {
  positionId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const usePositionDetailQuery = ({
  positionId,
  workspaceId,
  idToken,
  enabled = true,
}: UsePositionDetailQueryParams) => {
  return useQuery({
    queryKey: ['position', positionId],
    queryFn: async () => {
      if (!positionId || !workspaceId || !idToken) {
        throw new Error('Position ID, Workspace ID, and token are required');
      }

      return await positionService.getPosition(positionId, workspaceId, idToken);
    },
    enabled: enabled && !!positionId && !!workspaceId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
